import React, { useState, useEffect } from "react";
import { User, X, Mail, Key, Edit3, Check, AlertTriangle } from "lucide-react";
import { useUiLang } from "../../../context/UiLangContext";
import { useProfile } from "../hooks/useProfile";
import { Spinner } from "./Shared/Icons";
import { ErrorAlert } from "../../../components/ErrorAlert";

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userServiceUrl: string;
}

export function ProfileModal({ isOpen, onClose, userServiceUrl }: ProfileModalProps) {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  const [nameInput, setNameInput] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const {
    profile,
    loading,
    saving,
    error,
    setError,
    success,
    updateProfile,
    resetState,
  } = useProfile(userServiceUrl, isOpen);

  useEffect(() => {
    if (!isOpen) {
      setNameInput("");
      setIsEditing(false);
    } else if (profile && !isEditing) {
      setNameInput(profile.name || "");
    }
  }, [isOpen, profile, isEditing]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await updateProfile(nameInput);
    if (success) {
      setIsEditing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-header flex items-center justify-center p-4">
      <div
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-md w-full shadow-2xl overflow-hidden animate-[modal-appear_0.2s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <User size={18} className="text-[var(--accent)]" />
            <h2 id="profile-modal-title" className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              {isEn ? "My Profile" : "Mi Perfil"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
            aria-label={isEn ? "Close" : "Cerrar"}
          >
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <ErrorAlert error={error} onDismiss={() => setError("")} className="mb-4" />
          )}

          {success && (
            <div className="flex items-start gap-2.5 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-semibold mb-4 leading-normal">
              <Check size={15} className="mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Spinner sizeClass="w-8 h-8" borderClass="border-4 border-[var(--accent)]/20 border-t-[var(--accent)]" />
              <p className="text-xs text-[var(--text-muted)] font-mono animate-pulse">
                {isEn ? "Loading profile..." : "Cargando perfil..."}
              </p>
            </div>
          ) : profile ? (
            <div className="flex flex-col gap-5">
              {/* Profile Card Summary */}
              <div className="flex items-center gap-4 bg-[var(--surface)] border border-[var(--border)] p-4 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-[var(--accent-muted)] border border-[var(--accent)]/30 flex items-center justify-center text-[var(--accent)] text-lg font-bold">
                  {profile.name ? profile.name.charAt(0).toUpperCase() : "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-[var(--text)] truncate">
                    {profile.name || (isEn ? "No Name Provided" : "Sin Nombre")}
                  </h3>
                  <p className="text-xs text-[var(--text-muted)] font-mono truncate">
                    {profile.email}
                  </p>
                </div>
              </div>

              {/* Details List */}
              <div className="flex flex-col gap-4 bg-black/5 dark:bg-white/[0.02] border border-[var(--border)] p-4 rounded-xl">
                {/* ID/Sub */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-[var(--text-muted)] font-mono select-all truncate">
                    {profile.user_id}
                  </span>
                </div>

                {/* Email */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-mono font-bold text-[var(--text-dim)] uppercase tracking-wider flex items-center gap-1.5">
                    <Mail size={10} />
                    Email
                  </span>
                  <span className="text-xs text-[var(--text)]">
                    {profile.email}
                  </span>
                </div>

                {/* Name */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-mono font-bold text-[var(--text-dim)] uppercase tracking-wider flex items-center gap-1.5">
                    <User size={10} />
                    {isEn ? "Name" : "Nombre"}
                  </span>

                  {isEditing ? (
                    <form onSubmit={handleUpdateProfile} className="flex gap-2 mt-1">
                      <input
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        disabled={saving}
                        className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-lg py-2 px-3 text-xs text-[var(--text)] placeholder-[var(--text-dim)]  focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all"
                        placeholder={isEn ? "Enter name..." : "Ingresa nombre..."}
                        autoFocus
                      />
                      <button
                        type="submit"
                        disabled={saving}
                        className="px-3.5 bg-accent hover:bg-accent/80 disabled:opacity-50 text-white rounded-lg flex items-center justify-center transition-all active:scale-95 shadow-md"
                        title={isEn ? "Save" : "Guardar"}
                      >
                        {saving ? <Spinner /> : <Check size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditing(false);
                          setNameInput(profile.name || "");
                        }}
                        disabled={saving}
                        className="px-3 border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] rounded-lg hover:bg-[var(--surface-hover)] transition-all"
                      >
                        <X size={14} />
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-xs text-[var(--text)] font-semibold">
                        {profile.name || <i>{isEn ? "Not specified" : "No especificado"}</i>}
                      </span>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-1.5 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-dim)] hover:text-[var(--text)] transition-all flex items-center justify-center border border-transparent hover:border-[var(--border)]"
                        title={isEn ? "Edit name" : "Editar nombre"}
                      >
                        <Edit3 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-xs text-[var(--text-dim)]">
              {isEn ? "No profile data loaded." : "No se pudieron cargar los datos."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
